import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { RegimeBadge, Change, Loader, Empty, DataTag, LiveBadge, LivePrice } from "@/components/widgets";
import useLivePrices from "@/hooks/useLivePrices";
import { Plus, Star, ArrowRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [watch, setWatch] = useState([]);
  const [add, setAdd] = useState("");

  const loadWatch = () => api.get("/watchlist").then((r) => setWatch(r.data.items)).catch(() => {});
  useEffect(() => { api.get("/market/overview").then((r) => setOverview(r.data)).catch(() => {}); loadWatch(); }, []);

  const addWatch = async (e) => {
    e.preventDefault();
    const t = add.trim().toUpperCase();
    if (!t) return;
    try { await api.post("/watchlist", { ticker: t }); setAdd(""); loadWatch(); toast.success(`${t} added`); }
    catch { toast.error("Could not add"); }
  };
  const removeWatch = async (t) => { await api.delete(`/watchlist/${t}`); loadWatch(); };

  const { prices: livePrices, connected } = useLivePrices(watch.map((w) => w.ticker));

  if (!overview) return <Loader />;
  const { regime, indices, top_gainers, top_losers } = overview;

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs">Market Overview</div>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter">Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <DataTag kind="AI" />
          <RegimeBadge regime={regime.regime} />
        </div>
      </div>

      {/* Regime banner */}
      <div className="mt-4 border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <div className="label-xs">Market Regime</div>
            <div className="mt-1 font-heading text-xl font-semibold">{regime.regime?.replace(/_/g, " ")}</div>
          </div>
          <div><div className="label-xs">S&P 20d</div><div className="mono mt-1"><Change value={regime.spy_ret_20d} /></div></div>
          <div><div className="label-xs">Realized Vol</div><div className="mono mt-1 text-lg">{regime.realized_vol}%</div></div>
          <div><div className="label-xs">60d Drawdown</div><div className="mono mt-1 text-lg neg">{regime.drawdown_60d}%</div></div>
          <p className="ml-auto max-w-md text-sm text-muted-foreground">{regime.description}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Indices */}
        <div className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3 label-xs">Indices</div>
          {indices.map((i) => (
            <button key={i.ticker} onClick={() => nav(`/stock/${i.ticker}`)}
              className="flex w-full items-center justify-between px-4 py-3 border-b border-border/50 hover:bg-white/[0.03] transition-colors duration-200 text-left">
              <div><div className="mono font-medium">{i.ticker}</div><div className="text-xs text-muted-foreground">{i.name}</div></div>
              <div className="text-right"><div className="mono">${i.price}</div><Change value={i.change_pct} className="text-xs" /></div>
            </button>
          ))}
        </div>

        {/* Movers */}
        <div className="border border-border bg-card">
          <div className="border-b border-border px-4 py-3 label-xs">Top Gainers · Today</div>
          {top_gainers.map((m) => <MoverRow key={m.ticker} m={m} onClick={() => nav(`/stock/${m.ticker}`)} />)}
          <div className="border-y border-border px-4 py-3 label-xs">Top Losers · Today</div>
          {top_losers.map((m) => <MoverRow key={m.ticker} m={m} onClick={() => nav(`/stock/${m.ticker}`)} />)}
        </div>

        {/* Watchlist */}
        <div className="border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="label-xs flex items-center gap-1.5"><Star size={13} weight="fill" className="text-signal-yellow" /> Watchlist</span>
            <LiveBadge connected={connected} />
          </div>
          <form onSubmit={addWatch} className="flex gap-2 p-3 border-b border-border">
            <Input data-testid="watchlist-input" value={add} onChange={(e) => setAdd(e.target.value)} placeholder="Add ticker" className="h-8 bg-background rounded-sm mono uppercase" />
            <Button data-testid="watchlist-add-btn" type="submit" size="sm" className="h-8 bg-volt hover:bg-volt/90 rounded-sm px-2"><Plus size={16} /></Button>
          </form>
          {watch.length === 0 ? <Empty>No symbols tracked yet</Empty> : watch.map((w) => {
            const live = livePrices[w.ticker];
            const price = live?.price ?? w.price;
            const change = live?.change_pct ?? w.change_pct;
            return (
            <div key={w.ticker} className="group flex items-center justify-between px-4 py-2.5 border-b border-border/50 hover:bg-white/[0.03] transition-colors duration-200">
              <button onClick={() => nav(`/stock/${w.ticker}`)} className="text-left flex-1">
                <div className="mono font-medium">{w.ticker}</div>
                <div className="text-xs text-muted-foreground"><LivePrice value={price} /></div>
              </button>
              <div className="flex items-center gap-3">
                <Change value={change} className="text-xs" />
                <button onClick={() => removeWatch(w.ticker)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-signal-red transition-opacity">×</button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      <button onClick={() => nav("/scanner")} data-testid="goto-scanner"
        className="mt-6 flex items-center gap-2 border border-volt/40 bg-volt/5 px-4 py-3 text-sm text-volt hover:bg-volt/10 transition-colors duration-200 rounded-sm">
        Run the AI Market Scanner to find ranked opportunities <ArrowRight size={16} />
      </button>
    </div>
  );
}

function MoverRow({ m, onClick }) {
  return (
    <button onClick={onClick} className="flex w-full items-center justify-between px-4 py-2.5 border-b border-border/50 hover:bg-white/[0.03] transition-colors duration-200 text-left">
      <div><div className="mono font-medium">{m.ticker}</div><div className="text-xs text-muted-foreground truncate max-w-[120px]">{m.name}</div></div>
      <div className="text-right"><div className="mono text-sm">${m.price}</div><Change value={m.change_pct} className="text-xs" /></div>
    </button>
  );
}
